import { Button, Modal, Spin, Typography } from "antd";
import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
  logoutUser,
  resendVerifyUser,
  verifyUser,
} from "../../../redux/Slices/AuthSlice";
import "./VerifyModal.scss";

/** B6: the api issues 6-digit codes. */
export const VERIFICATION_CODE_LENGTH = 6;
/** Resend is offered again after this long, e.g. once a code expired. */
export const RESEND_COOLDOWN_MS = 30_000;

const CODE_LABEL_ID = "st-verify-code-label";

const VerifyModal: FC = () => {
  const { user, verifyUserLoading, resendLoading } = useAppSelector(
    (state) => state.auth
  );
  const [resendHidden, setResendHidden] = useState(false);
  const [otp, setOtp] = useState("");
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (!resendHidden) return;
    const timer = setTimeout(() => setResendHidden(false), RESEND_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [resendHidden]);

  const onResend = async () => {
    setResendHidden(true);
    const sent = await dispatch(resendVerifyUser()).unwrap();
    if (!sent) setResendHidden(false);
  };

  const onLogoutUser = () => {
    dispatch(logoutUser());
  };

  return (
    <Modal
      open={user && !user.verified}
      title={t("auth:verify.title")}
      centered
      footer={[]}
      closeIcon={false}
    >
      <Typography.Paragraph className="st-verify__intro">
        <Trans
          i18nKey="auth:verify.subtitleWithEmail"
          values={{ email: user?.email }}
          components={[<strong key="0" />]}
        />
      </Typography.Paragraph>
      <div id={CODE_LABEL_ID} className="st-verify__label">
        {t("auth:verify.codeLabel")}
      </div>
      <div className="st-verify__code" role="group" aria-labelledby={CODE_LABEL_ID}>
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={VERIFICATION_CODE_LENGTH}
          containerStyle="st-verify__otp-row"
          inputStyle="st-otp"
          skipDefaultStyles
          // The library's inline width (1em) is dropped: VerifyModal.scss sizes the boxes. Phones get
          // a numeric keypad, and the first box offers the emailed code as autofill (the library
          // spreads a code typed or pasted into one box across all six).
          renderInput={({ style: _libraryStyle, ...props }, index) => (
            <input
              {...props}
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              aria-label={t("auth:verify.digitLabel", {
                n: index + 1,
                total: VERIFICATION_CODE_LENGTH,
              })}
            />
          )}
        />
      </div>
      <Button
        block
        type="primary"
        loading={verifyUserLoading}
        disabled={otp.length !== VERIFICATION_CODE_LENGTH}
        onClick={() => dispatch(verifyUser({ code: otp }))}
      >
        {t("auth:verify.submit")}
      </Button>
      <div className="st-verify__footer">
        <Typography.Text type="secondary" className="st-verify__notice">
          {t("auth:verify.legacyCodeNotice")}
        </Typography.Text>
        {!resendHidden && (
          <Typography.Text className="st-verify__line">
            {t("auth:verify.didntReceive")}
            <Button type="link" size="small" onClick={onResend}>
              {t("auth:verify.resend")}
            </Button>
          </Typography.Text>
        )}
        {resendLoading && <Spin className="st-verify__spin" />}
        <Typography.Text className="st-verify__line">
          {t("auth:verify.or")}
          <Button type="link" size="small" onClick={onLogoutUser}>
            {t("auth:verify.logout")}
          </Button>
        </Typography.Text>
      </div>
    </Modal>
  );
};

export default VerifyModal;
